/* eslint-disable effector/no-useless-methods */
import { merge, sample } from 'effector';
import { delay, pending } from 'patronum';
import { format } from 'date-fns';
import {
  $token,
  history,
  NEW_TICKET_MESSAGE,
  readNotifications,
  signout,
  TICKET_UPDATED,
} from '@features/common';
import { ClassesData } from '@features/tickets-class-select';
import { fxGetList as fxGetPrepaymentType } from '@features/tickets-prepayment-type-select';
import { PriorityData } from '@features/tickets-priority-select';
import { fxGetList as fxGetStatuses } from '@features/tickets-status-select';
import { convertUTCDateToLocalDate } from '@tools/formatDateWithZone';
import { ticketApi } from '../../api';
import {
  AssigneesItem,
  CreateTicketPayload,
  Initiator,
  Repeat,
  TicketResponse,
  Value,
  Ticket,
  Objects,
} from '../../interfaces';
import { formatPayloadDate } from '../../libs';
import { fxUpdateAdditionalFieldsTicket } from '../additional-fields/model';
import { $openedId } from '../detail-sdui-opened-id/model';
import { changeTab } from '../detail/detail.model';
import { addClicked, path, fxGetTypes } from '../page/page.model';
import {
  fxUpdateAssignessTicket,
  fxGetUsersInfo,
  getUsersInfo,
  $usersInfo,
} from '../ticket-assignees/ticket-assignees.model';
import { fxGetRates, getRates } from '../ticket-declarant';
import {
  fxSetTicketPriority,
  fxChangeTicketStatus,
  fxChangeAppealTicket,
  fxSetEscalation,
  fxSetDescription,
  fxSetTicketType,
  fxCheckAppeal,
  fxCheckEscalation,
  fxSetTicketSla,
  fxSetTicketRate,
} from '../ticket-info/ticket-info.model';
import {
  fxUploadAttachment,
  fxCreateInvoice,
  fxGetInvoices,
  fxUpdateInvoice,
  fxDeleteInvoice,
  fxClarifiedPrice,
} from '../ticket-invoice/ticket-invoice.model';
import { fxGetObjects, getObjects } from '../ticket-objects/ticket-objects.model';
import {
  fxSetRepeat,
  fxGetRepeat,
  getRepeat,
} from '../ticket-repeat/ticket-repeat.model';
import {
  fxGetAvailableTehzor,
  fxSendTicketToTehzor,
} from '../ticket-tehzor/ticket-tehzor.model';
import { fxSetPlannedDate } from '../ticket-time/ticket-time.model';
import { fxGetTicketTmc, getTicketTmc, fxSetTmc } from '../ticket-tmc/ticket-tmc.model';
import { fxGetTicketWorks, fxSetTicketWorks } from '../ticket-works/ticket-works.model';
import {
  DATE_TIME_FIELDS,
  FIELDS_BY_ID,
  FILE_FIELDS,
  IGNORE_FIELDS_PARAM,
} from './init-additional-fields-data';
import {
  $error,
  $isErrorDialogOpen,
  $isLoading,
  $mode,
  $ticket,
  $ticketData,
  API_URL,
  changedErrorDialogVisibility,
  changedMode,
  cloneTicket,
  exportTicket,
  exportOrder,
  fxCreateTicket,
  fxGetTicketById,
  fxNotifySupport,
  needTicket,
  notifySupport,
  TicketGate,
  pageMounted,
  pageUnmounted,
  entityApi,
  $isLoadingTicket,
  $isLoadingCreateTicket,
} from './ticket.model';

const CHANGE_ASSIGNESS_TIMEOUT = 1000;
const CHANGE_STATUS_TIMEOUT = 1000;

fxGetTicketById.use(ticketApi.getTicketById);
fxCreateTicket.use(ticketApi.createTicket);
fxGetUsersInfo.use(ticketApi.getUsersInfo);
fxSetTicketPriority.use(ticketApi.setTicketPriority);
fxUpdateAssignessTicket.use(
  (props) =>
    new Promise((resolve) => {
      setTimeout(() => {
        resolve(ticketApi.changeAssignessTicket(props));
      }, CHANGE_ASSIGNESS_TIMEOUT);
    })
);
fxChangeTicketStatus.use(
  (props) =>
    new Promise((resolve) => {
      setTimeout(() => {
        resolve(ticketApi.changeTicketStatus(props));
      }, CHANGE_STATUS_TIMEOUT);
    })
);
fxChangeAppealTicket.use(ticketApi.changeAppeal);
fxGetRates.use(ticketApi.getTicketRates);
fxSetRepeat.use(ticketApi.setRepeat);
fxGetRepeat.use(ticketApi.getRepeat);
fxSetPlannedDate.use(ticketApi.setPlannedDate);
fxGetTicketTmc.use(ticketApi.getTicketTmc);
fxSetTmc.use(ticketApi.setTmc);
fxGetObjects.use(ticketApi.getObjects);
fxSetEscalation.use(ticketApi.setEscalation);
fxNotifySupport.use(ticketApi.notifySupport);
fxGetTicketWorks.use(ticketApi.getWorks);
fxSetDescription.use(ticketApi.setDescription);
fxCheckEscalation.use(ticketApi.checkEscalation);
fxCheckAppeal.use(ticketApi.checkAppeal);
fxSetTicketType.use(ticketApi.updateTicketTypes);
fxSetTicketSla.use(ticketApi.setTicketSla);
fxUploadAttachment.use(ticketApi.uploadFile);
fxCreateInvoice.use(ticketApi.createInvoice);
fxGetInvoices.use(ticketApi.getInvoices);
fxUpdateInvoice.use(ticketApi.updateInvoice);
fxDeleteInvoice.use(ticketApi.deleteInvoice);
fxSetTicketWorks.use(ticketApi.setWorks);
fxClarifiedPrice.use(ticketApi.clarifiedPrice);
fxSetTicketRate.use(ticketApi.setTicketRate);
fxGetAvailableTehzor.use(ticketApi.getAvailableTehzor);
fxSendTicketToTehzor.use(ticketApi.sendTicketToTehzor);
fxUpdateAdditionalFieldsTicket.use(ticketApi.changeAdditionalFieldsTicket);

const errorOccured = merge([
  fxGetTicketById.fail,
  fxCreateTicket.fail,
  fxGetUsersInfo.fail,
  fxSetTicketPriority.fail,
  fxUpdateAssignessTicket.fail,
  fxChangeTicketStatus.fail,
  fxChangeAppealTicket.fail,
  fxGetRates.fail,
  fxSetRepeat.fail,
  fxGetRepeat.fail,
  fxSetPlannedDate.fail,
  fxSetTmc.fail,
  fxGetObjects.fail,
  fxSetEscalation.fail,
  fxNotifySupport.fail,
  fxSetDescription.fail,
  fxCheckEscalation.fail,
  fxCheckAppeal.fail,
  fxGetTicketTmc.fail,
  fxGetTicketWorks.fail,
  fxSetTicketType.fail,
  fxUploadAttachment.fail,
  fxCreateInvoice.fail,
  fxGetInvoices.fail,
  fxUpdateInvoice.fail,
  fxDeleteInvoice.fail,
  fxSetTicketWorks.fail,
  fxClarifiedPrice.fail,
  fxSetTicketRate.fail,
  fxGetAvailableTehzor.fail,
  fxSendTicketToTehzor.fail,
  fxUpdateAdditionalFieldsTicket.fail,
]);

$isLoadingCreateTicket
  .on(
    pending({
      effects: [fxCreateTicket],
    }),
    (_, status) => status
  )
  .reset([pageUnmounted, signout]);

$isLoading
  .on(
    pending({
      effects: [
        fxGetTicketById,
        fxCreateTicket,
        fxGetUsersInfo,
        fxSetTicketPriority,
        fxUpdateAssignessTicket,
        fxChangeTicketStatus,
        fxChangeAppealTicket,
        fxGetRates,
        fxSetRepeat,
        fxGetRepeat,
        fxSetPlannedDate,
        fxGetTicketTmc,
        fxSetTmc,
        fxGetObjects,
        fxSetEscalation,
        fxNotifySupport,
        fxGetTicketWorks,
        fxSetDescription,
        fxCheckEscalation,
        fxCheckAppeal,
        fxSetTicketType,
        fxUploadAttachment,
        fxCreateInvoice,
        fxGetInvoices,
        fxUpdateInvoice,
        fxDeleteInvoice,
        fxSetTicketWorks,
        fxClarifiedPrice,
        fxSetTicketRate,
        fxGetAvailableTehzor,
        fxSendTicketToTehzor,
        fxUpdateAdditionalFieldsTicket,
      ],
    }),
    (_, status) => status
  )
  .reset([pageUnmounted, signout]);

$isLoadingTicket
  .on(
    pending({
      effects: [fxGetTicketById],
    }),
    (_, status) => status
  )
  .reset([pageUnmounted, signout]);

$error
  .on(errorOccured, (_, { error }) => error)
  .on(fxGetTicketTmc.fail, (_, { error }) => error)
  .reset([pageUnmounted, signout]);

$isErrorDialogOpen
  .on(changedErrorDialogVisibility, (_, visibility) => visibility)
  .on(errorOccured, () => true)
  .reset([pageUnmounted, signout]);

$mode
  .on(changedMode, (_, mode) => mode)
  .on(pageMounted, () => 'view')
  .reset([signout, fxCreateTicket.done, fxUpdateAssignessTicket.done, changeTab]);

sample({
  clock: [pageMounted, needTicket, TicketGate.state],
  filter: ({ ticketId }) => ticketId !== null && !Number.isNaN(Number(ticketId)),
  fn: ({ ticketId }) => ({ ticket: { id: ticketId } }),
  target: fxGetTicketById,
});

sample({
  clock: fxUpdateAssignessTicket,
  filter: ({ users }) => !users.length,
  fn: () => [],
  target: $usersInfo,
});

const formatDate = (date: string) => {
  const localDate = new Date(convertUTCDateToLocalDate(new Date(date)));

  return format(new Date(localDate), "yyyy-MM-dd'T'HH:mm");
};

const formatAdditionalFieldsToTicket = (fields: Record<string, unknown> | undefined) => {
  if (!fields) return {};

  const result = {} as any;
  Object.keys(fields).forEach((key) => {
    if (DATE_TIME_FIELDS.includes(key)) {
      result[key] = new Date(fields[key] as string);

      return;
    }

    result[key] = fields[key];
  });

  return result;
};

const formatTicket = (result: TicketResponse) => {
  const { ticket } = result;

  if (ticket.id) {
    getRepeat(ticket.id);
    getTicketTmc(ticket.id);
  }

  if (ticket.objects.length > 0) {
    getObjects(ticket.objects);
  }

  if ((ticket.initiator as Initiator)?.id) {
    getRates({ ticket: { id: ticket.id } });
  }

  if (ticket.assignees.length > 0) {
    getUsersInfo(ticket.assignees);
  }

  return {
    id: ticket.id,
    description: ticket.description || '-',
    class: ticket.class || '',
    types: [ticket.types[0]?.id],
    formattedTypes: '-',
    priority: ticket.priority || '',
    sla: ticket?.sla ? formatDate(ticket.sla) : '',
    assignees: '',
    origin: ticket.origin,
    status: ticket.status || '',
    isActiveSupport: Boolean(ticket?.support?.is_active),
    date_create_ticket: formatDate(ticket.created_at),
    planned_start_at: ticket?.planned_start_at
      ? new Date(convertUTCDateToLocalDate(new Date(ticket?.planned_start_at)))
      : '',
    planned_end_at: ticket?.planned_end_at
      ? new Date(convertUTCDateToLocalDate(new Date(ticket?.planned_end_at)))
      : '',
    object: '',
    house: '',
    apartment: '',
    property: '',
    ticket_number:
      ticket?.source && ticket?.source?.id
        ? `${ticket.number}/${ticket?.source?.id}`
        : ticket.number,
    is_appeal: Boolean(ticket.is_appeal),
    is_escalation_enabled: Boolean(ticket.is_escalation_enabled),
    initiator: ticket.initiator,
    until_repeat: '',
    type_repeat: '',
    every_repeat: '',
    actual_end_at: ticket.actual_end_at ? formatDate(ticket.actual_end_at) : '',
    channel: ticket.channel.id,
    enterprise: ticket.enterprise_id,
    invoices: [],
    inspection_id: ticket.inspection_id,
    inspection_number: ticket?.inspection?.number,
    inspection: ticket.inspection,
    source: ticket.source,
    support: ticket.support,
    fields: formatAdditionalFieldsToTicket(ticket.fields),
  };
};

$ticketData
  .on(fxGetTicketById.done, (_, { result }) => formatTicket(result) as any)
  .on(fxUpdateAssignessTicket.done, (_, { result }) => formatTicket(result) as any)
  .reset([signout, addClicked, fxGetTicketById.pending]);

sample({
  clock: [pageMounted, needTicket],
  target: fxGetStatuses,
});

// триггер после получения заявки
sample({
  clock: fxGetTicketById.done,
  filter: ({ result }) => Boolean(result.ticket.id),
  fn: ({ result }) => result.ticket.id as number,
  target: fxGetAvailableTehzor,
});

sample({
  clock: fxCreateTicket.done,
  fn: ({ result }) => {
    window.location.href = `${path}/${result.ticket.id}`;
  },
});

const delayedRedirect = delay({ source: fxGetTicketById.fail, timeout: 3000 });

delayedRedirect.watch(() => {
  history.push(path);
});

sample({
  clock: cloneTicket,
  fn: () => {
    history.push(path);
  },
});

$ticket
  .on(cloneTicket, (state) => {
    const isArchivedTypes = Boolean([...state.types].reverse()[0]?.archived);

    state.id = null;
    state.sla = '';
    state.status = '';
    state.date_create_ticket = '';

    if (isArchivedTypes) {
      state.types = [];
    }

    return state;
  })
  .reset([signout, addClicked]);

sample({
  clock: exportTicket,
  source: { ticket: $ticket, token: $token, openedId: $openedId },
  fn: ({ ticket: { id }, token, openedId }) => {
    const newVersion = localStorage.getItem('ticketsNewVersion') === 'new' ? true : false;
    const ticketId = newVersion ? openedId : id;

    window.open(
      `${API_URL}/v1/tck/bms/tickets/item/export/?token=${token}` +
        `&ticket[id]=${ticketId}` +
        `&lang=${localStorage.getItem('lang')}`
    );
  },
});

sample({
  clock: notifySupport,
  fn: (id) => ({ ticket: { id } }),
  target: fxNotifySupport,
});

sample({
  source: $ticket,
  filter: ({ id }) => Boolean(id),
  fn: ({ id }) => ({
    id,
    event: TICKET_UPDATED,
  }),
  target: readNotifications,
});

sample({
  source: $ticket,
  filter: ({ id }) => Boolean(id),
  fn: ({ id }) => ({
    id,
    event: NEW_TICKET_MESSAGE,
  }),
  target: readNotifications,
});

sample({
  clock: pageMounted,
  target: fxGetPrepaymentType,
});

sample({
  clock: exportOrder,
  source: { ticket: $ticket, token: $token, openedId: $openedId },
  fn: ({ ticket: { id }, token, openedId }) => {
    const newVersion = localStorage.getItem('ticketsNewVersion') === 'new' ? true : false;
    const ticketId = newVersion ? openedId : id;

    window.open(
      `${API_URL}/v1/tck/bms/tickets/item/export-order/?token=${token}` +
        `&ticket[id]=${ticketId}` +
        `&lang=${localStorage.getItem('lang')}`
    );
  },
});

sample({
  clock: fxGetTicketById.done,
  target: fxGetTypes,
});

const formatObjects = (
  object: Value,
  house: Value,
  apartment: Value,
  property: Value
) => {
  const objects = [];

  if (object) {
    objects.push({
      id: object?.id || object,
      type: 'complex',
    });
  }

  if (house) {
    objects.push({ id: house?.id || house, type: 'building' });
  }

  if (apartment) {
    objects.push({
      id: apartment?.id || apartment,
      type: 'apartment',
    });
  }

  if (property) {
    objects.push({
      id: property?.id || property,
      type: 'property',
    });
  }

  const reversedObjects = objects.reverse()[0];

  return reversedObjects ? [reversedObjects as Objects] : [];
};

const formatRepeat = (
  until_repeat: string,
  every_repeat: string,
  type_repeat: string | Repeat
) => {
  const repeat: { until?: string; every?: string; type?: string } = {};

  if (until_repeat) {
    repeat.until = until_repeat;
  }

  if (every_repeat) {
    repeat.every = every_repeat;
  }

  if (type_repeat) {
    repeat.type = (type_repeat as Repeat)?.id;
  }

  return repeat;
};

export const formatAdditionalFieldsToPayload = (
  fields: Record<string, unknown> | undefined
) => {
  if (!fields) return {};

  const result = [] as any;
  const PREFIX = 'im';
  Object.keys(fields).forEach((key) => {
    if (key === IGNORE_FIELDS_PARAM) return;
    if (
      Object.values(
        FIELDS_BY_ID[(fields[IGNORE_FIELDS_PARAM] as string) ?? ''] ?? {}
      ).includes(key) === false
    )
      return;

    if (DATE_TIME_FIELDS.includes(key)) {
      result.push({ uuid: key, value: formatPayloadDate(fields[key] as Date) });

      return;
    }

    if (FILE_FIELDS.includes(key)) {
      result.push({
        uuid: key,
        value: (fields[key] as { id: number }[]).map(({ id }) => `${PREFIX}${id}`),
      });

      return;
    }

    result.push({ uuid: key, value: fields[key] });
  });

  return result;
};

sample({
  clock: entityApi.create,
  source: $isLoadingCreateTicket,
  filter: (isLoadingCreateTicket) => !isLoadingCreateTicket,
  fn: (_, params: Ticket): CreateTicketPayload => {
    const findedType = [...params.types].reverse().find((item: Value) => item);

    const assignees =
      params.assignees.length > 0
        ? (params.assignees as AssigneesItem[]).map((item) => {
            if ((item as any)?.user) {
              return (item as any).user.id;
            }

            return item.id;
          })
        : [];

    const payload: CreateTicketPayload = {
      id: params.id,
      description: params.description,
      origin: typeof params.origin === 'object' ? params.origin.name : params.origin,
      status: typeof params.status === 'object' ? params.status.id : params.status,
      types: findedType?.id ? [findedType.id] : [],
      sla: params.sla && formatPayloadDate(params.sla),
      assignees,
      class:
        typeof params.class === 'object'
          ? (params.class as ClassesData)?.id
          : params.class,
      priority:
        typeof params.priority === 'object'
          ? (params.priority as PriorityData)?.id
          : params.priority,
      planned_start_at: params?.planned_start_at
        ? formatPayloadDate(params.planned_start_at)
        : '',
      planned_end_at: params?.planned_end_at
        ? formatPayloadDate(params.planned_end_at)
        : '',
      objects: formatObjects(
        params.object as Value,
        params.house as Value,
        params.apartment as Value,
        params.property as Value
      ),
      is_appeal: Number(Boolean(params?.is_appeal)),
      initiator: (params.initiator as Initiator)?.id
        ? {
            id: (params.initiator as Initiator).id,
          }
        : null,
      // repeat: null,
      repeat: formatRepeat(params.until_repeat, params.every_repeat, params.type_repeat),
    };

    if (typeof params.enterprise === 'object' && Boolean(params.enterprise?.id)) {
      payload.enterprise_id = params.enterprise.id;
    }

    if (!!params.fields) {
      payload.fields = formatAdditionalFieldsToPayload(params.fields);
    }

    return payload;
  },
  target: fxCreateTicket,
});

sample({
  clock: fxNotifySupport.done,
  source: $ticket,
  filter: () => {
    const newVersion = localStorage.getItem('ticketsNewVersion') === 'new' ? true : false;

    return !newVersion;
  },
  fn: ({ id }) => ({ ticket: { id: id as number } }),
  target: fxGetTicketById,
});
